import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { auditService } from '../../services/auditService';
import { normaliseHeader } from '../../reports/engine';
import { readSourceFile } from '../Reports/reportSources';
import { downloadFile, fmtQty, safeFileName, todayStamp } from '../Reports/ui';
import { PageLoader } from '../../components/Loader';
import Pager, { usePaged } from '../../components/Pager';
import './RecoPanel.css';

const FILTERS = [
  { id: 'variance', label: '1st cut variance', test: (r) => r.variance !== 0 },
  { id: 'reco', label: 'Reconciled', test: (r) => r.hasReco },
  { id: 'pending', label: 'Variance, no reco yet', test: (r) => r.variance !== 0 && !r.hasReco },
  { id: 'final', label: 'Final difference', test: (r) => r.final !== 0 },
  { id: 'all', label: 'All items', test: () => true },
];

const round3 = (v) => Math.round(v * 1000) / 1000;
const signed = (v) => (v > 0 ? `+${fmtQty(v)}` : fmtQty(v));
const tone = (v) => (v < 0 ? 'short' : v > 0 ? 'excess' : 'match');

/** Text of a quantity input -> number ('' = 0); null when not a number. */
function qtyOf(text) {
  const t = String(text ?? '').trim();
  if (t === '') return 0;
  const n = Number(t);
  return Number.isFinite(n) ? n : null;
}

function when(iso) {
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '' : d.toLocaleString('en-IN', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' });
}

const csvCell = (v) => {
  const s = String(v ?? '');
  return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
};

/**
 * Reco tab: after the 1st cut variance (counted − book), Admin / Data ASA
 * enter what reconciliation found per item (+ or − quantity) and why
 * (contra SKU reason). Recount = counted + reco; final diff = recount − book.
 * These feed the variance report on the Reports page.
 */
function RecoPanel({ auditId, canEdit, refreshKey }) {
  const [items, setItems] = useState(null);
  const [reasons, setReasons] = useState([]);
  const [saved, setSaved] = useState(new Map());
  const [edits, setEdits] = useState(new Map());
  const [filter, setFilter] = useState('variance');
  const [search, setSearch] = useState('');
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [saving, setSaving] = useState(false);
  const [bookFile, setBookFile] = useState(null);
  const fileInput = useRef(null);

  const load = useCallback(async () => {
    try {
      const [comparison, reco] = await Promise.all([auditService.getComparison(auditId, 'book'), auditService.getReco(auditId)]);
      setItems(comparison?.items || []);
      setBookFile(comparison?.book || null);
      setReasons(reco?.reasons || []);
      setSaved(new Map((reco?.entries || []).map((e) => [String(e.ean), e])));
      setError('');
    } catch (err) {
      setError(err.message);
      setItems((current) => current || []);
    }
  }, [auditId]);

  useEffect(() => {
    load();
  }, [load, refreshKey]);

  // Current value of an item's reco: unsaved edit, else saved entry.
  const valueOf = useCallback(
    (ean) => {
      const edit = edits.get(ean);
      if (edit) return edit;
      const entry = saved.get(ean);
      return entry
        ? { reco_qty: entry.reco_qty ? String(entry.reco_qty) : '', reason: entry.reason || '', remark: entry.remark || '' }
        : { reco_qty: '', reason: '', remark: '' };
    },
    [edits, saved],
  );

  const rows = useMemo(
    () =>
      (items || []).map((item) => {
        const ean = String(item.ean);
        const v = valueOf(ean);
        const reco = qtyOf(v.reco_qty) ?? 0;
        const recount = round3(Number(item.counted_qty || 0) + reco);
        return {
          ...item,
          ean,
          value: v,
          reco,
          recount,
          final: round3(recount - Number(item.book_qty || 0)),
          hasReco: Boolean(v.reco_qty !== '' && Number(v.reco_qty) !== 0) || Boolean(v.reason) || Boolean(v.remark),
          dirty: edits.has(ean),
          entry: saved.get(ean),
        };
      }),
    [items, valueOf, edits, saved],
  );

  const counts = useMemo(() => Object.fromEntries(FILTERS.map((f) => [f.id, rows.filter(f.test).length])), [rows]);
  const totals = useMemo(() => {
    const withVar = rows.filter((r) => r.variance !== 0);
    return {
      variance: withVar.length,
      reconciled: rows.filter((r) => r.hasReco).length,
      recoNet: round3(rows.reduce((t, r) => t + r.reco, 0)),
      final: rows.filter((r) => r.final !== 0).length,
      firstNet: round3(rows.reduce((t, r) => t + Number(r.variance || 0), 0)),
      finalNet: round3(rows.reduce((t, r) => t + r.final, 0)),
    };
  }, [rows]);

  const shown = useMemo(() => {
    const test = (FILTERS.find((f) => f.id === filter) || FILTERS[0]).test;
    const term = search.trim().toLowerCase();
    return rows
      .filter(test)
      .filter((r) => !term || `${r.ean} ${r.article || ''} ${r.description || ''}`.toLowerCase().includes(term))
      .sort((a, b) => Math.abs(b.variance) - Math.abs(a.variance) || a.ean.localeCompare(b.ean));
  }, [rows, filter, search]);

  const paged = usePaged(shown, { pageSize: 50, sizes: [25, 50, 100, 200], resetKey: `${filter}|${search}`, storageKey: 'portal-reco' });
  const pageRows = paged.items;

  const edit = (ean, field, value) => {
    setMessage('');
    setEdits((prev) => {
      const next = new Map(prev);
      const base = next.get(ean) || valueOf(ean);
      const updated = { ...base, [field]: value };
      const entry = saved.get(ean);
      const same =
        (entry ? String(entry.reco_qty || '') : '') === String(qtyOf(updated.reco_qty) || '') &&
        (entry?.reason || '') === updated.reason &&
        (entry?.remark || '') === updated.remark.trim();
      if (same) next.delete(ean);
      else next.set(ean, updated);
      return next;
    });
  };

  const badQty = [...edits.values()].filter((v) => qtyOf(v.reco_qty) === null).length;

  const save = async () => {
    if (!edits.size || badQty) return;
    setSaving(true);
    setError('');
    try {
      const entries = [...edits.entries()].map(([ean, v]) => ({
        ean,
        reco_qty: qtyOf(v.reco_qty),
        reason: v.reason,
        remark: v.remark.trim(),
      }));
      for (let i = 0; i < entries.length; i += 5000) {
        // eslint-disable-next-line no-await-in-loop
        await auditService.saveReco(auditId, entries.slice(i, i + 5000));
      }
      setEdits(new Map());
      setMessage(`Saved reco for ${fmtQty(entries.length)} item${entries.length === 1 ? '' : 's'}.`);
      await load();
    } catch (err) {
      setError(`Not saved: ${err.message}`);
    } finally {
      setSaving(false);
    }
  };

  const downloadSheet = () => {
    const header = ['EAN', 'Article', 'Description', 'Book Qty', 'Counted Qty', '1st Cut Variance', 'Reco Qty', 'Reco Reason', 'Reco Remark'];
    const lines = [header.join(',')];
    shown.forEach((r) =>
      lines.push(
        [r.ean, r.article || '', r.description || '', r.book_qty, r.counted_qty, r.variance, r.value.reco_qty, r.value.reason, r.value.remark]
          .map(csvCell)
          .join(','),
      ),
    );
    downloadFile(`Reco sheet ${safeFileName(String(auditId))} ${todayStamp()}.csv`, `﻿${lines.join('\r\n')}`, 'text/csv');
  };

  // Upload a filled reco sheet (EAN + Reco Qty, optional reason / remark): goes into the unsaved changes for review.
  const uploadSheet = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;
    setError('');
    setMessage('');
    try {
      const preview = await readSourceFile(file);
      const all = await preview.readAll(0);
      const headerIndex = all.slice(0, 15).findIndex((row) => row.some((c) => ['ean', 'barcode'].includes(normaliseHeader(c))));
      if (headerIndex < 0) throw new Error('No "EAN" column found in the first rows of the sheet.');
      const head = all[headerIndex].map((h) => normaliseHeader(h));
      const col = (...names) => head.findIndex((h) => names.includes(h));
      const cEan = col('ean', 'barcode');
      const cQty = col('reco qty', 'reco', 'reco count', 'reco quantity');
      const cReason = col('reco reason', 'reason', 'remarks', 'contra sku');
      const cRemark = col('reco remark', 'remark', 'note');
      if (cQty < 0) throw new Error('No "Reco Qty" column found.');
      const known = new Set((items || []).map((i) => String(i.ean)));
      const lower = new Map(reasons.map((r) => [r.toLowerCase(), r]));
      let added = 0;
      let unknown = 0;
      let bad = 0;
      const next = new Map(edits);
      all.slice(headerIndex + 1).forEach((row) => {
        const ean = String(row[cEan] ?? '').trim();
        if (!ean) return;
        if (!known.has(ean)) {
          unknown += 1;
          return;
        }
        const qtyText = String(row[cQty] ?? '').trim();
        if (qtyOf(qtyText) === null) {
          bad += 1;
          return;
        }
        const reasonText = cReason >= 0 ? String(row[cReason] ?? '').trim() : '';
        const v = {
          reco_qty: qtyText,
          reason: lower.get(reasonText.toLowerCase()) || reasonText,
          remark: cRemark >= 0 ? String(row[cRemark] ?? '').trim() : '',
        };
        const cur = valueOf(ean);
        if (cur.reco_qty === v.reco_qty && cur.reason === v.reason && cur.remark === v.remark) return;
        next.set(ean, v);
        added += 1;
      });
      setEdits(next);
      setFilter('reco');
      setMessage(
        `${fmtQty(added)} item${added === 1 ? '' : 's'} filled from ${file.name} — check and press Save.` +
          (unknown ? ` ${fmtQty(unknown)} EAN${unknown === 1 ? '' : 's'} not in this schedule skipped.` : '') +
          (bad ? ` ${fmtQty(bad)} row${bad === 1 ? '' : 's'} with a non-number Reco Qty skipped.` : ''),
      );
    } catch (err) {
      setError(err.message);
    }
  };

  if (items === null) return <PageLoader label="Loading items…" />;

  return (
    <section className="audit-portal__panel live-panel reco">
      <header className="live-panel__header">
        <div>
          <h2>Reco — reconciliation after the 1st cut</h2>
          <p className="live-muted">
            1st cut variance = counted − book. Enter what reconciliation found per item (+ found, − not there) and the
            reason. Recount = counted + reco; final difference = recount − book. The variance report on the Reports page
            uses these.
          </p>
        </div>
        <div className="reco__tools">
          <button type="button" className="compare-button compare-button--ghost" onClick={downloadSheet} disabled={!shown.length}>
            Download reco sheet
          </button>
          {canEdit ? (
            <>
              <button type="button" className="compare-button compare-button--ghost" onClick={() => fileInput.current?.click()}>
                Upload reco sheet
              </button>
              <input ref={fileInput} type="file" accept=".csv,.txt,.xlsx,.xlsb" hidden onChange={uploadSheet} />
            </>
          ) : null}
        </div>
      </header>

      {!bookFile ? (
        <p className="live-notice">No book stock uploaded for this schedule yet — the 1st cut variance needs it (Comparison tab).</p>
      ) : null}

      <div className="reco__cards">
        <article>
          <span>Items with 1st cut variance</span>
          <strong>{fmtQty(totals.variance)}</strong>
          <small>Net {signed(totals.firstNet)}</small>
        </article>
        <article className="reco__card--reco">
          <span>Reconciled</span>
          <strong>{fmtQty(totals.reconciled)}</strong>
          <small>Net reco {signed(totals.recoNet)}</small>
        </article>
        <article className={totals.final ? 'reco__card--short' : 'reco__card--match'}>
          <span>Items with final difference</span>
          <strong>{fmtQty(totals.final)}</strong>
          <small>Net {signed(totals.finalNet)}</small>
        </article>
      </div>

      <div className="reco__bar">
        <div className="live-chips" role="group" aria-label="Show">
          {FILTERS.map((f) => (
            <button key={f.id} type="button" aria-pressed={filter === f.id} onClick={() => setFilter(f.id)}>
              {f.label} ({fmtQty(counts[f.id])})
            </button>
          ))}
        </div>
        <input
          type="search"
          className="reco__search"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search EAN, article, description…"
          aria-label="Search items"
        />
      </div>

      {error ? <p className="compare-error">{error}</p> : null}
      {message ? <p className="reco__message">{message}</p> : null}

      {canEdit && edits.size ? (
        <div className="reco__savebar" role="status">
          <span>
            <strong>{fmtQty(edits.size)}</strong> unsaved change{edits.size === 1 ? '' : 's'}
            {badQty ? <em> · {fmtQty(badQty)} with a quantity that is not a number</em> : null}
          </span>
          <button type="button" className="compare-link" onClick={() => setEdits(new Map())} disabled={saving}>
            Discard
          </button>
          <button type="button" className="compare-button" onClick={save} disabled={saving || badQty > 0}>
            {saving ? 'Saving…' : 'Save reco'}
          </button>
        </div>
      ) : null}

      {shown.length === 0 ? (
        <p className="compare-empty">{search ? `Nothing matches “${search}”.` : 'No items here.'}</p>
      ) : (
        <div className="reco__table-wrap" ref={paged.anchorRef}>
          <table className="reco__table">
            <thead>
              <tr>
                <th>Item</th>
                <th className="num">Book</th>
                <th className="num">Counted</th>
                <th className="num">1st cut</th>
                <th className="num reco__col-reco">Reco qty</th>
                <th className="reco__col-reco">Reason</th>
                <th className="reco__col-reco">Remark</th>
                <th className="num">Recount</th>
                <th className="num">Final diff</th>
              </tr>
            </thead>
            <tbody>
              {pageRows.map((r) => {
                const bad = qtyOf(r.value.reco_qty) === null;
                const reasonOptions = r.value.reason && !reasons.includes(r.value.reason) ? [...reasons, r.value.reason] : reasons;
                return (
                  <tr key={r.ean} className={r.dirty ? 'is-dirty' : ''}>
                    <td className="reco__item">
                      <code>{r.ean}</code>
                      {r.by_article ? <small> · article</small> : null}
                      <span>{r.description || '—'}</span>
                      {r.entry && !r.dirty ? (
                        <small className="reco__by">
                          {r.entry.updated_by_name || '—'} · {when(r.entry.updated_at)}
                        </small>
                      ) : null}
                    </td>
                    <td className="num">{fmtQty(r.book_qty)}</td>
                    <td className="num">{fmtQty(r.counted_qty)}</td>
                    <td className={`num reco__v reco__v--${tone(r.variance)}`}>{signed(r.variance)}</td>
                    <td className="num reco__col-reco">
                      {canEdit ? (
                        <input
                          type="text"
                          inputMode="decimal"
                          className={`reco__qty${bad ? ' is-bad' : ''}`}
                          value={r.value.reco_qty}
                          placeholder="0"
                          aria-label={`Reco quantity for ${r.ean}`}
                          onChange={(e) => edit(r.ean, 'reco_qty', e.target.value)}
                        />
                      ) : (
                        signed(r.reco)
                      )}
                    </td>
                    <td className="reco__col-reco">
                      {canEdit ? (
                        <select value={r.value.reason} onChange={(e) => edit(r.ean, 'reason', e.target.value)} aria-label={`Reason for ${r.ean}`}>
                          <option value="">—</option>
                          {reasonOptions.map((reason) => (
                            <option key={reason} value={reason}>
                              {reason}
                            </option>
                          ))}
                        </select>
                      ) : (
                        r.value.reason || '—'
                      )}
                    </td>
                    <td className="reco__col-reco">
                      {canEdit ? (
                        <input
                          type="text"
                          className="reco__remark"
                          value={r.value.remark}
                          maxLength={500}
                          placeholder="Optional"
                          aria-label={`Remark for ${r.ean}`}
                          onChange={(e) => edit(r.ean, 'remark', e.target.value)}
                        />
                      ) : (
                        r.value.remark || '—'
                      )}
                    </td>
                    <td className="num">{fmtQty(r.recount)}</td>
                    <td className="num">
                      <span className={`reco__pill reco__pill--${tone(r.final)}`}>
                        {r.final === 0 ? 'Match' : signed(r.final)}
                      </span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      <Pager paged={paged} noun="items" />
    </section>
  );
}

export default RecoPanel;
